import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import { useForm, router } from "@inertiajs/react";
import { Field, Input, Textarea, Checkbox } from "@/components/ui/Field";
import Button from "@/components/ui/Button";
import Panel from "@/components/ui/Panel";
import { useState } from "react";

type EventSetting = {
  event_name: string;
  event_tagline: string | null;
  event_location: string | null;
  event_starts_on: string | null;
  event_ends_on: string | null;
  // Where the postponed half of the program runs. A US event that chose to
  // move answers to this window too.
  intl_event_starts_on: string | null;
  intl_event_ends_on: string | null;
  season_starts_on: string | null;
  season_ends_on: string | null;
  support_email: string | null;
  support_channel_name: string | null;
  support_channel_url: string | null;
  guide_url: string | null;
  og_image_url: string | null;
  shipping_enabled: boolean;
  project_creation_enabled: boolean;
  project_creation_closed_message: string | null;
  events_enabled: boolean;
  organizer_applications_open: boolean;
  organizer_applications_us_only: boolean;
  rsvps_open: boolean;
  submissions_open: boolean;
  submissions_us_only: boolean;
  listings_us_only: boolean;
  organizer_boards_open: boolean;
  custom_event_pages_enabled: boolean;
  guides_locked: boolean;
  guides_locked_message: string | null;
  projects_enabled: boolean;
  review_enabled: boolean;
  shop_enabled: boolean;
  budget_per_rsvp_cents: number;
  budget_per_hour_cents: number;
  prefunding_per_site_cents: number;
  min_attendee_age: number;
  max_attendee_age: number;
};

// Grouped so it's obvious which switches run Boba Bash and which belong to the
// dormant ticket economy.
const TOGGLE_GROUPS: {
  heading: string;
  note?: string;
  toggles: { key: keyof EventSetting; label: string; hint?: string }[];
}[] = [
  {
    heading: "Boba Bash",
    toggles: [
      {
        key: "events_enabled",
        label: "Events",
        hint: "Shows the Events and Organize sections at all.",
      },
      {
        key: "organizer_applications_open",
        label: "Applications open",
        hint: "Lets people apply to be a point of contact for their city.",
      },
      {
        key: "organizer_applications_us_only",
        label: "US applications only",
        hint: "Refuses new applications for cities outside the US while the rest of the program runs in October. Does nothing unless applications are open, and never blocks an existing organizer from editing their own event.",
      },
      {
        key: "rsvps_open",
        label: "RSVPs open",
        hint: "Off means approved events are visible and shareable, but nobody can sign up yet. Cancelling always works.",
      },
      {
        key: "submissions_open",
        label: "Hand-ins open",
        hint: "Lets attendees hand in the site they built and fills the public gallery. Each event still opens on its own day, in its own time zone, and closes a week after.",
      },
      {
        key: "submissions_us_only",
        label: "US hand-ins only",
        hint: "Holds hand-ins to the cities actually running while the rest of the program waits for October. Does nothing unless hand-ins are open. Turn it off once the October events start.",
      },
      {
        key: "listings_us_only",
        label: "List US cities only",
        hint: "Keeps the events directory, the landing page and the nightly Slack leaderboard to the cities actually running while the rest of the program waits for October. Unlisted is not closed: every event link still opens and still takes RSVPs. Turn it off once the October events start.",
      },
      {
        key: "organizer_boards_open",
        label: "Organizer boards open",
        hint: "Off closes the season: every organizer can still open and read their board, but nothing on it can be changed (details, roster, check-in, team, page, messages). HQ can still edit. Nothing is deleted.",
      },
      {
        key: "custom_event_pages_enabled",
        label: "Let organizers write HTML",
        hint: "Adds a raw HTML/CSS pane to the event page editor. It renders in a sandbox that blocks JavaScript. Off hides the pane and withholds any code already written, without deleting it.",
      },
      {
        key: "guides_locked",
        label: "Hold the guides",
        hint: "On means everyone but an admin gets a holding page instead of the handbooks. Admins still read them, so they can be rewritten in place.",
      },
    ],
  },
  {
    heading: "Shipping for budget",
    note: "Organizers link Hackatime and ship projects; approved hours pay into their event's budget at the hourly rate below.",
    toggles: [
      {
        key: "projects_enabled",
        label: "Projects",
        hint: "Shows the Projects section and lets organizers ship. Off means no one can reach it.",
      },
      {
        key: "review_enabled",
        label: "Project review",
        hint: "Hours only pay out once a reviewer approves them, so this needs to be on for budget to move.",
      },
      {
        key: "project_creation_enabled",
        label: "Project creation",
        hint: "Off means existing projects can still be shipped, but no new ones start.",
      },
      { key: "shipping_enabled", label: "Shipping" },
    ],
  },
  {
    heading: "Ticket economy",
    note: "Switched off for Boba Bash. Returns as Pearls.",
    toggles: [{ key: "shop_enabled", label: "Shop" }],
  },
];

const TEXT_FIELDS: {
  key: keyof EventSetting;
  label: string;
  hint?: string;
  type?: string;
}[] = [
  {
    key: "event_name",
    label: "Event name",
    hint: "Shown in the page title, sidebar, onboarding and OG tags.",
  },
  {
    key: "event_tagline",
    label: "Tagline",
    hint: "One-liner on the dashboard and in the meta description.",
  },
  { key: "event_location", label: "Location" },
  {
    key: "event_starts_on",
    label: "Starts on",
    type: "date",
    hint: "The window an event that hasn't been postponed has to run in.",
  },
  { key: "event_ends_on", label: "Ends on", type: "date" },
  {
    key: "intl_event_starts_on",
    label: "Postponed window starts on",
    type: "date",
    hint: "Where every non-US event moved to, and where a US organizer who chose the extra time books instead. Leave both blank to send postponed events back to the window above.",
  },
  {
    key: "intl_event_ends_on",
    label: "Postponed window ends on",
    type: "date",
  },
  {
    key: "budget_per_rsvp_cents",
    label: "Fallback budget per verified RSVP (cents)",
    type: "number",
    hint: "800 = $8.00. Only paid on events with a confirmed venue, and only where the country has no rate on the grant rate card.",
  },
  {
    key: "budget_per_hour_cents",
    label: "Budget per approved coding hour (cents)",
    type: "number",
    hint: "500 = $5.00. Paid on hours a reviewer has approved, credited to the event the project was shipped for.",
  },
  {
    key: "prefunding_per_site_cents",
    label: "Pre-funding per approved site (cents)",
    type: "number",
    hint: "500 = $5.00. The international grant: paid per site handed in before the event starts, once a reviewer approves it. Those events earn nothing per RSVP, and this is flat worldwide rather than indexed by country.",
  },
  {
    key: "guides_locked_message",
    label: "What the holding page says",
    hint: "Leave blank for the default.",
  },
  { key: "min_attendee_age", label: "Minimum attendee age", type: "number" },
  { key: "max_attendee_age", label: "Maximum attendee age", type: "number" },
  {
    key: "season_starts_on",
    label: "Season starts on",
    type: "date",
    hint: "Only coding logged on or after this date counts towards budget. Moving it changes what every unshipped project is worth.",
  },
  {
    key: "season_ends_on",
    label: "Season ends on",
    type: "date",
    hint: "Drives the countdown. Leave blank to hide it.",
  },
  { key: "support_email", label: "Support email" },
  {
    key: "support_channel_name",
    label: "Support channel",
    hint: "Without the #.",
  },
  { key: "support_channel_url", label: "Support channel URL" },
  {
    key: "guide_url",
    label: "Project guide URL",
    hint: "Linked from the ship checklist.",
  },
  { key: "og_image_url", label: "OG image URL" },
];

export default function AdminEvent({ setting }: { setting: EventSetting }) {
  const [resyncing, setResyncing] = useState(false);
  const { data, setData, patch, processing, errors } = useForm<EventSetting>({
    ...setting,
  });

  return (
    <Layout>
      <PageHeading
        eyebrow="Admin"
        title="Settings"
        subtitle="Everything here is event-specific copy and toggles. Changing it takes effect immediately - no deploy needed."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          patch("/admin/settings");
        }}
        className="mt-8 flex max-w-3xl flex-col gap-6"
      >
        {TEXT_FIELDS.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            hint={field.hint}
            error={errors[field.key]}
          >
            <Input
              type={field.type ?? "text"}
              value={(data[field.key] as string) ?? ""}
              onChange={(e) => setData(field.key, e.target.value as never)}
            />
          </Field>
        ))}

        <Field
          label="Project creation closed message"
          hint="Shown when project creation is off. Admins and debt-role users are never blocked."
        >
          <Textarea
            value={data.project_creation_closed_message ?? ""}
            onChange={(e) =>
              setData("project_creation_closed_message", e.target.value)
            }
            rows={2}
          />
        </Field>

        {TOGGLE_GROUPS.map((group) => (
          <div key={group.heading} className="flex flex-col gap-3">
            <div className="border-line/20 border-b pb-2">
              <h2 className="font-display text-subheading text-ink font-bold">
                {group.heading}
              </h2>
              {group.note && (
                <p className="text-caption text-ink-soft">{group.note}</p>
              )}
            </div>

            {group.toggles.map((toggle) => (
              <label key={toggle.key} className="flex items-start gap-3">
                <Checkbox
                  checked={Boolean(data[toggle.key])}
                  onChange={(e) => setData(toggle.key, e.target.checked)}
                />
                <span className="flex flex-col">
                  <span className="text-body text-ink font-bold">
                    {toggle.label}
                  </span>
                  {toggle.hint && (
                    <span className="text-caption text-ink-soft">
                      {toggle.hint}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        ))}

        <Button type="submit" disabled={processing} className="self-start">
          {processing ? "Saving…" : "Save"}
        </Button>
      </form>

      <Panel title="sync.sh" className="mt-8">
        <div className="flex flex-col gap-3">
          <p className="text-body text-ink-muted">
            Re-pushes every organizer, event and RSVP to Airtable. The sweep
            runs hourly on its own, but it only fires while the worker is up -
            run it here after a burst of applications, or if the base looks
            behind.
          </p>
          <Button
            variant="secondary"
            disabled={resyncing}
            className="self-start"
            onClick={() => {
              setResyncing(true);
              router.post(
                "/admin/settings/resync",
                {},
                { preserveScroll: true, onFinish: () => setResyncing(false) },
              );
            }}
          >
            {resyncing ? "Queueing…" : "Resync Airtable now"}
          </Button>
        </div>
      </Panel>
    </Layout>
  );
}
